import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import ErrorDisplay from './ErrorDisplay';
import LoadingSpinner from './LoadingSpinner';
import AuthLanguageSwitcher from './AuthLanguageSwitcher';
import { WarningCircle, CheckCircle, ArrowLeft, Check, X } from '@phosphor-icons/react';
import { createPasswordSchema, PASSWORD_MIN_LENGTH } from '../../lib/passwordPolicy';
import { ValidationError } from '../../types/auth';

type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

type TokenStatus = 'checking' | 'valid' | 'invalid';

const PASSWORD_REQUIREMENTS = [
  { id: 'length', test: (value: string) => value.length >= PASSWORD_MIN_LENGTH },
  { id: 'lower', test: (value: string) => /[a-z]/.test(value) },
  { id: 'upper', test: (value: string) => /[A-Z]/.test(value) },
  { id: 'digit', test: (value: string) => /\d/.test(value) },
];

const PasswordChecklist: React.FC<{ password: string }> = ({ password }) => {
  const { t } = useTranslation('auth');
  return (
    <ul className="auth-checklist" aria-label={t('reset.checklistLabel')}>
      {PASSWORD_REQUIREMENTS.map(({ id, test }) => {
        const met = test(password);
        return (
          <li key={id} className={`auth-checklist-item ${met ? 'auth-checklist-item-met' : ''}`}>
            <span className="auth-checklist-icon" aria-hidden>
              {met ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
            </span>
            <span className="auth-checklist-label">{t(`reset.requirement.${id}`)}</span>
          </li>
        );
      })}
    </ul>
  );
};

const ResetPasswordForm: React.FC = () => {
  const { resetPassword, verifyResetToken, isLoading, error, clearError, retryLastAction } = useAuth();
  const { t } = useTranslation('auth');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>('checking');

  const resetPasswordSchema = useMemo(
    () =>
      z
        .object({
          password: createPasswordSchema(t),
          confirmPassword: z.string().min(1, t('validation.confirmRequired')),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('validation.passwordsMismatch'),
          path: ['confirmPassword'],
        }),
    [t]
  );

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const passwordValue = watch('password');

  useEffect(() => {
    let cancelled = false;

    const checkToken = async () => {
      if (!token) {
        navigate('/forgot-password', { replace: true });
        return;
      }

      clearError();
      setTokenStatus('checking');
      try {
        const { valid } = await verifyResetToken(token);
        if (cancelled) return;
        setTokenStatus(valid ? 'valid' : 'invalid');
      } catch {
        if (cancelled) return;
        setTokenStatus('invalid');
      }
    };

    checkToken();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, clearError, verifyResetToken]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    try {
      await resetPassword(token, data.password);
      setIsSubmitted(true);
    } catch (error) {
      // Surface server-side field errors next to their inputs when available.
      const fieldErrors = (error as any)?.details?.errors as ValidationError[] | undefined;
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(({ field, message }) => {
          if (field === 'password' || field === 'confirmPassword') {
            setError(field, { type: 'manual', message: message || t('common.invalidValue') });
          }
        });
      }
    }
  };

  const renderShell = (title: string, subtitle: string | null, body: React.ReactNode) => (
    <div className="auth-background">
      <div className="auth-background-overlay"></div>
      <AuthLanguageSwitcher />
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo-commission-fr.png" alt="CNCLT" />
          </div>
          <h1 className="auth-title">{title}</h1>
          {subtitle && <p className="auth-subtitle">{subtitle}</p>}
        </div>
        {body}
      </div>
    </div>
  );

  if (isSubmitted) {
    return renderShell(
      t('reset.successTitle'),
      t('reset.successSubtitle'),
      <>
        <div className="auth-alert auth-alert-success" role="status">
          <span className="auth-alert-icon">
            <CheckCircle size={20} weight="fill" aria-hidden />
          </span>
          <p className="auth-alert-body">
            {t('reset.successBody')}
          </p>
        </div>
        <div className="text-center">
          <Link to="/login" className="auth-button-primary">
            {t('common.login')}
          </Link>
        </div>
      </>
    );
  }

  if (tokenStatus === 'checking') {
    return renderShell(
      t('reset.checkingTitle'),
      null,
      <div className="auth-form" role="status" aria-live="polite">
        <span className="sr-only">{t('reset.checkingSr')}</span>
        <div className="auth-form-group">
          <div className="auth-skeleton h-4 w-32"></div>
          <div className="auth-skeleton h-12 w-full"></div>
        </div>
        <div className="auth-form-group">
          <div className="auth-skeleton h-4 w-44"></div>
          <div className="auth-skeleton h-12 w-full"></div>
        </div>
        <div className="auth-skeleton h-12 w-full"></div>
      </div>
    );
  }

  if (tokenStatus === 'invalid') {
    return renderShell(
      t('reset.invalidTitle'),
      null,
      <>
        <div className="auth-alert auth-alert-error" role="alert">
          <span className="auth-alert-icon">
            <WarningCircle size={20} weight="fill" aria-hidden />
          </span>
          <div className="auth-alert-content">
            <p className="auth-alert-title">{t('reset.invalidAlertTitle')}</p>
            <p className="auth-alert-body">
              {t('reset.invalidAlertBody')}
            </p>
          </div>
        </div>
        <div className="text-center">
          <Link to="/forgot-password" className="auth-button-primary">
            {t('reset.requestNewLink')}
          </Link>
        </div>
        <div className="text-center mt-4">
          <Link to="/login" className="auth-link text-sm">
            <ArrowLeft size={16} weight="bold" aria-hidden className="mr-1 inline" />
            {t('common.backToLogin')}
          </Link>
        </div>
      </>
    );
  }

  return renderShell(
    t('reset.title'),
    t('reset.subtitle'),
    <>
      <ErrorDisplay
        error={error}
        onRetry={retryLastAction}
        onDismiss={clearError}
      />

      <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="auth-form-group">
          <label className="auth-form-label">{t('reset.newPasswordLabel')}</label>
          <input
            type="password"
            autoComplete="new-password"
            className={`auth-form-input ${errors.password ? 'auth-form-input-error' : ''}`}
            placeholder={t('common.passwordPlaceholder')}
            disabled={isLoading}
            {...register('password')}
          />
          <PasswordChecklist password={passwordValue} />
          {errors.password && (
            <p className="auth-form-error" role="alert">
              <WarningCircle size={14} weight="bold" aria-hidden />
              {errors.password.message}
            </p>
          )}
        </div>

        <div className="auth-form-group">
          <label className="auth-form-label">{t('reset.confirmPasswordLabel')}</label>
          <input
            type="password"
            autoComplete="new-password"
            className={`auth-form-input ${errors.confirmPassword ? 'auth-form-input-error' : ''}`}
            placeholder={t('common.passwordPlaceholder')}
            disabled={isLoading}
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && (
            <p className="auth-form-error" role="alert">
              <WarningCircle size={14} weight="bold" aria-hidden />
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          className={`auth-button-primary ${isLoading ? 'auth-loading' : ''}`}
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingSpinner size="sm" text={t('reset.submitting')} />
          ) : (
            t('reset.submit')
          )}
        </button>

        <div className="text-center">
          <Link to="/login" className="auth-link text-sm">
            {t('common.backToLogin')}
          </Link>
        </div>
      </form>
    </>
  );
};

export default ResetPasswordForm;
